import { APPROVAL_REQUIRED_RISKS, type RiskClass } from '@ralph/contracts'

/**
 * The two questions a bulk enable has to answer, kept apart.
 *
 * They were one predicate, called `isActing`, listing the four classes that require
 * approval — and the confirmation then described that count as *the ones that change
 * something*. Those are not the same set, and the class in the gap is the worst one to lose:
 * `reversible_write` is labelled **Changes something that can be undone** and is the only
 * class that both writes to the world *and* runs without anybody being asked. A dozen of
 * them selected produced the sentence "None of them change anything."
 *
 * So:
 *
 *  - **`changesSomething`** — anything but `read`. This is what the word "change" in the
 *    confirmation means, and it is derived from the one class that provably cannot.
 *  - **`runsUnattended`** — the complement of the platform's own approval list, *imported*
 *    rather than restated. Restating it is precisely how the two drifted apart: the original
 *    predicate was a correct copy of `APPROVAL_REQUIRED_RISKS` under a name that claimed
 *    something else, and a copy under a wrong name is a copy nobody re-reads.
 *
 * Kept out of the component because a confirmation sentence that is wrong is not a rendering
 * bug — it is an administrator switching on write access believing they switched on reads —
 * and that belongs somewhere a test can reach without a DOM.
 */

/** Everything except `read`. `read`'s own contract is that it cannot change anything. */
export const changesSomething = (risk: RiskClass): boolean => risk !== 'read'

/**
 * Whether this class executes without a human seeing it first.
 *
 * The negation of the runtime's gate, read from the shared constant so the copy on this
 * screen cannot promise something the orchestrator does not enforce.
 */
export const runsUnattended = (risk: RiskClass): boolean => !APPROVAL_REQUIRED_RISKS.includes(risk)

export type BulkCounts = {
  total: number
  /** How many will change something in the world. */
  writing: number
  /** How many of those will do it without anybody being asked. A subset of `writing`. */
  unattended: number
}

/**
 * Counts the three numbers the confirmation needs.
 *
 * `unattended` counts only capabilities that *also* write, so it can never exceed `writing`
 * and can never be inflated by the read-only ones — which run unattended too, and about
 * which nobody needs warning.
 */
export const countBulk = (selection: ReadonlyArray<{ risk: RiskClass }>): BulkCounts => {
  const writes = selection.filter((entry) => changesSomething(entry.risk))
  return {
    total: selection.length,
    writing: writes.length,
    unattended: writes.filter((entry) => runsUnattended(entry.risk)).length
  }
}
